import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import {
    FaMusic,
    FaArrowLeft,
    FaUser,
    FaCompactDisc,
    FaCalendarAlt,
    FaClock,
    FaHashtag,
    FaKey,
    FaEdit,
    FaFileAlt,
    FaInfoCircle,
    FaPen,
    FaHeadphones,
    FaVideo,
    FaExternalLinkAlt,
    FaExclamationTriangle,
    FaStar,
    FaFire,
    FaMicrophone,
    FaTags,
    FaHeart
} from "react-icons/fa";
import "./DisplayLyrics.css";

const DisplayLyrics = () => {
    const navigate = useNavigate();
    const { isrcKey } = useParams(); // This is the song_id
    const [song, setSong] = useState(null);
    const [artist, setArtist] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchSongData = async () => {
        setLoading(true);
        try {
            const res = await allAPiServicesCall.getSongById(isrcKey, getAuthConfig());
            const songData = res?.data ?? null;
            setSong(songData);

            // Fetch artist details if we have an artist_id
            if (songData?.artist_id) {
                try {
                    const artistRes = await allAPiServicesCall.getArtistById(songData.artist_id, getAuthConfig());
                    setArtist(artistRes?.data ?? null);
                } catch (artistError) {
                    console.error("fetchArtist error:", artistError);
                }
            }
        } catch (error) {
            console.error("fetchSongData error:", error);
            setSong(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isrcKey) {
            fetchSongData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isrcKey]);

    const formatDuration = (seconds) => {
        if (!seconds) return null;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        try {
            return new Date(dateStr).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric"
            });
        } catch {
            return null;
        }
    };

    const getGenres = (genre) => {
        if (!genre) return [];
        if (Array.isArray(genre)) return genre.filter(Boolean);
        if (typeof genre === "string") {
            return genre.split(",").map(g => g.trim()).filter(Boolean);
        }
        return [];
    };

    const getArrayField = (field) => {
        if (!field) return [];
        if (Array.isArray(field)) return field.filter(Boolean);
        if (typeof field === "string") {
            return field.split(",").map(item => item.trim()).filter(Boolean);
        }
        return [];
    };

    const normalizedLyrics = (song?.lyrics || "")
        .replaceAll("\\n", "\n")
        .replaceAll("\r\n", "\n");

    const hasLyrics = !!normalizedLyrics.trim();

    const coverImage = song?.song_image_downloaded_link || song?.coverImage || song?.cover_image || null;
    const artistName = artist?.artist_name || song?.artist_details?.artist_name || song?.artist_name || song?.artist || "Unknown Artist";
    const artistImage = artist?.artist_image_downloaded_link || artist?.artistImage || null;
    const albumName = song?.album_details?.album_name || song?.album_name || null;
    const songTitle = song?.song_name || song?.title || song?.name || "Untitled";
    const genres = getGenres(song?.genre);
    const writers = getArrayField(song?.writers);
    const producers = getArrayField(song?.producers);
    const tags = getArrayField(song?.tags);
    const moods = getArrayField(song?.moods);

    if (loading) {
        return (
            <CommonLayout>
                <div className="lyrics-page">
                    <div className="lyrics-loading">
                        <div className="lyrics-loading-spinner"></div>
                        <p className="lyrics-loading-text">Loading song details...</p>
                    </div>
                </div>
            </CommonLayout>
        );
    }

    return (
        <CommonLayout>
            <div className="lyrics-page">
                {/* Back Button */}
                <button className="lyrics-back-btn" onClick={() => navigate(-1)}>
                    <FaArrowLeft /> Back
                </button>

                {song ? (
                    <>
                        {/* Header Section with Cover & Song Info */}
                        <div className="lyrics-header">
                            <div className="lyrics-cover-wrapper">
                                {coverImage ? (
                                    <img
                                        src={coverImage}
                                        alt={songTitle}
                                        className="lyrics-cover"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = "/no-image.png";
                                        }}
                                    />
                                ) : (
                                    <div className="lyrics-cover-placeholder">
                                        <FaMusic />
                                    </div>
                                )}
                            </div>

                            <div className="lyrics-info">
                                <h1 className="lyrics-song-title">{songTitle}</h1>

                                <p className="lyrics-artist-name">
                                    <FaUser /> {artistName}
                                </p>

                                {albumName && (
                                    <p className="lyrics-album-name">
                                        <FaCompactDisc /> {albumName}
                                    </p>
                                )}

                                {/* Meta Info */}
                                <div className="lyrics-meta-grid">
                                    {song?.releaseDate && (
                                        <div className="lyrics-meta-item">
                                            <FaCalendarAlt />
                                            <span><strong>{formatDate(song.releaseDate)}</strong></span>
                                        </div>
                                    )}
                                    {song?.song_duration && (
                                        <div className="lyrics-meta-item">
                                            <FaClock />
                                            <span><strong>{formatDuration(song.song_duration)}</strong></span>
                                        </div>
                                    )}
                                    {song?.bpm && (
                                        <div className="lyrics-meta-item">
                                            <FaHashtag />
                                            <span><strong>{song.bpm}</strong> BPM</span>
                                        </div>
                                    )}
                                    {song?.key && (
                                        <div className="lyrics-meta-item">
                                            <FaKey />
                                            <span>Key: <strong>{song.key}</strong></span>
                                        </div>
                                    )}
                                </div>

                                {/* Genres */}
                                {genres.length > 0 && (
                                    <div className="lyrics-genres">
                                        {genres.map((g, i) => (
                                            <span key={i} className="lyrics-genre-tag">{g}</span>
                                        ))}
                                    </div>
                                )}

                                {/* Flags */}
                                <div className="lyrics-flags">
                                    {song?.explicit && (
                                        <span className="lyrics-flag lyrics-flag-explicit">
                                            <FaExclamationTriangle /> Explicit
                                        </span>
                                    )}
                                    {song?.isSingle && (
                                        <span className="lyrics-flag lyrics-flag-single">
                                            <FaMicrophone /> Single
                                        </span>
                                    )}
                                    {song?.isFeatured && (
                                        <span className="lyrics-flag lyrics-flag-featured">
                                            <FaStar /> Featured
                                        </span>
                                    )}
                                    {song?.isTrending && (
                                        <span className="lyrics-flag lyrics-flag-trending">
                                            <FaFire /> Trending
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Main Content Grid */}
                        <div className="lyrics-content-grid">
                            {/* Lyrics Card */}
                            <div className="lyrics-main-card">
                                <div className="lyrics-card-header">
                                    <div className="lyrics-card-header-icon">
                                        <FaFileAlt />
                                    </div>
                                    <h2 className="lyrics-card-title">Lyrics</h2>
                                </div>
                                <div className="lyrics-card-body">
                                    {hasLyrics ? (
                                        <pre className="lyrics-text">{normalizedLyrics}</pre>
                                    ) : (
                                        <div className="lyrics-empty">
                                            <div className="lyrics-empty-icon">
                                                <FaFileAlt />
                                            </div>
                                            <h3 className="lyrics-empty-title">No Lyrics Available</h3>
                                            <p className="lyrics-empty-text">
                                                The lyrics for this song have not been added yet.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Sidebar */}
                            <div className="lyrics-sidebar">
                                {/* Actions Card */}
                                <div className="lyrics-sidebar-card">
                                    <div className="lyrics-sidebar-header">
                                        <FaEdit />
                                        <h3 className="lyrics-sidebar-title">Actions</h3>
                                    </div>
                                    <div className="lyrics-sidebar-body">
                                        <div className="lyrics-actions-grid">
                                            <Link
                                                to={`/edit-song/${song.song_id}`}
                                                className="lyrics-action-btn lyrics-action-btn-primary"
                                            >
                                                <FaEdit /> Edit Song
                                            </Link>
                                            {song?.artist_id && (
                                                <Link
                                                    to={`/artist/albums-songs/${song.artist_id}`}
                                                    className="lyrics-action-btn lyrics-action-btn-secondary"
                                                >
                                                    <FaUser /> View Artist
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Song Details Card */}
                                <div className="lyrics-sidebar-card">
                                    <div className="lyrics-sidebar-header">
                                        <FaInfoCircle />
                                        <h3 className="lyrics-sidebar-title">Song Details</h3>
                                    </div>
                                    <div className="lyrics-sidebar-body">
                                        <div className="lyrics-details-list">
                                            <div className="lyrics-detail-item">
                                                <span className="lyrics-detail-label">Song ID</span>
                                                <span className="lyrics-detail-value">#{song.song_id}</span>
                                            </div>
                                            {song?.artist_id && (
                                                <div className="lyrics-detail-item">
                                                    <span className="lyrics-detail-label">Artist ID</span>
                                                    <span className="lyrics-detail-value">#{song.artist_id}</span>
                                                </div>
                                            )}
                                            {song?.album_id && (
                                                <div className="lyrics-detail-item">
                                                    <span className="lyrics-detail-label">Album ID</span>
                                                    <span className="lyrics-detail-value">#{song.album_id}</span>
                                                </div>
                                            )}
                                            <div className="lyrics-detail-item">
                                                <span className="lyrics-detail-label">Has Lyrics</span>
                                                <span className="lyrics-detail-value">{hasLyrics ? "Yes" : "No"}</span>
                                            </div>
                                            {song?.createdAt && (
                                                <div className="lyrics-detail-item">
                                                    <span className="lyrics-detail-label">Created</span>
                                                    <span className="lyrics-detail-value">{formatDate(song.createdAt)}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Credits Card */}
                                {(writers.length > 0 || producers.length > 0) && (
                                    <div className="lyrics-sidebar-card">
                                        <div className="lyrics-sidebar-header">
                                            <FaPen />
                                            <h3 className="lyrics-sidebar-title">Credits</h3>
                                        </div>
                                        <div className="lyrics-sidebar-body">
                                            <div className="lyrics-credits-list">
                                                {writers.length > 0 && (
                                                    <div className="lyrics-credit-item">
                                                        <span className="lyrics-credit-role">Writers</span>
                                                        <span className="lyrics-credit-names">{writers.join(", ")}</span>
                                                    </div>
                                                )}
                                                {producers.length > 0 && (
                                                    <div className="lyrics-credit-item">
                                                        <span className="lyrics-credit-role">Producers</span>
                                                        <span className="lyrics-credit-names">{producers.join(", ")}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Tags & Moods Card */}
                                {(tags.length > 0 || moods.length > 0) && (
                                    <div className="lyrics-sidebar-card">
                                        <div className="lyrics-sidebar-header">
                                            <FaTags />
                                            <h3 className="lyrics-sidebar-title">Tags & Moods</h3>
                                        </div>
                                        <div className="lyrics-sidebar-body">
                                            {moods.length > 0 && (
                                                <div style={{ marginBottom: tags.length > 0 ? "1rem" : 0 }}>
                                                    <p style={{ fontSize: "0.75rem", color: "#9CA3AF", marginBottom: "0.5rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                                        Moods
                                                    </p>
                                                    <div className="lyrics-tags-container">
                                                        {moods.map((mood, i) => (
                                                            <span key={i} className="lyrics-mood-tag">
                                                                <FaHeart style={{ fontSize: "0.6rem" }} /> {mood}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {tags.length > 0 && (
                                                <div>
                                                    <p style={{ fontSize: "0.75rem", color: "#9CA3AF", marginBottom: "0.5rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                                        Tags
                                                    </p>
                                                    <div className="lyrics-tags-container">
                                                        {tags.map((tag, i) => (
                                                            <span key={i} className="lyrics-tag">{tag}</span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* External Links Card */}
                                {(song?.audioPreviewUrl || song?.musicVideoUrl) && (
                                    <div className="lyrics-sidebar-card">
                                        <div className="lyrics-sidebar-header">
                                            <FaExternalLinkAlt />
                                            <h3 className="lyrics-sidebar-title">External Links</h3>
                                        </div>
                                        <div className="lyrics-sidebar-body">
                                            <div className="lyrics-external-links">
                                                {song?.audioPreviewUrl && (
                                                    <a
                                                        href={song.audioPreviewUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="lyrics-external-link"
                                                    >
                                                        <FaHeadphones />
                                                        <span className="lyrics-external-link-text">Audio Preview</span>
                                                        <FaExternalLinkAlt style={{ fontSize: "0.7rem" }} />
                                                    </a>
                                                )}
                                                {song?.musicVideoUrl && (
                                                    <a
                                                        href={song.musicVideoUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="lyrics-external-link"
                                                    >
                                                        <FaVideo />
                                                        <span className="lyrics-external-link-text">Music Video</span>
                                                        <FaExternalLinkAlt style={{ fontSize: "0.7rem" }} />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Description Card */}
                                {song?.description && (
                                    <div className="lyrics-sidebar-card">
                                        <div className="lyrics-sidebar-header">
                                            <FaInfoCircle />
                                            <h3 className="lyrics-sidebar-title">Description</h3>
                                        </div>
                                        <div className="lyrics-sidebar-body">
                                            <p className="lyrics-description-text">{song.description}</p>
                                        </div>
                                    </div>
                                )}

                                {/* Song Meaning Card */}
                                {song?.song_meaning && (
                                    <div className="lyrics-sidebar-card">
                                        <div className="lyrics-sidebar-header">
                                            <FaHeart />
                                            <h3 className="lyrics-sidebar-title">Song Meaning</h3>
                                        </div>
                                        <div className="lyrics-sidebar-body">
                                            <p className="lyrics-description-text">{song.song_meaning}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="lyrics-main-card">
                        <div className="lyrics-card-body">
                            <div className="lyrics-empty">
                                <div className="lyrics-empty-icon">
                                    <FaMusic />
                                </div>
                                <h3 className="lyrics-empty-title">Song Not Found</h3>
                                <p className="lyrics-empty-text">
                                    The song you are looking for could not be found or may have been removed.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </CommonLayout>
    );
};

export default DisplayLyrics;
